import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGridPremium,
  GridColDef,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { plGridLocales } from "utils/muiGridLocales";
import decreeService, { DecreeBox as Box } from "api/decreeService";
import { EDecreeType, IDecreeBoxItem } from "dto/Decree/IDecree";
import { ItemKind } from "dto/IKindItems";
import styles from "./styles.module.scss";

/** Multi skrzynka Przychodzących — statyczny filtr, pod którym otwieramy przesyłkę. */
const INCOMING_FILTER_ID = "1B8AE8D3-3209-497D-9526-27608FFFA84D";

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

type StatusKind = "open" | "warn" | "expired" | "done" | "muted";

const statusOf = (d: IDecreeBoxItem): { label: string; kind: StatusKind } => {
  if (d.Type === EDecreeType.Return) return { label: "Zwrot", kind: "muted" };
  if (d.RequiresConfirmation && d.ConfirmationTimestamp)
    return { label: "Potwierdzony", kind: "done" };
  if (d.IsClosed) return { label: "Wykonany", kind: "done" };
  if (d.RequiresConfirmation)
    return { label: "Czeka na potwierdzenie", kind: "open" };
  if (d.IsExpired) return { label: "Po terminie", kind: "expired" };
  if (d.IsAboutToExpire) return { label: "Zbliża się termin", kind: "warn" };
  if (d.IsPartlyClosed) return { label: "Wykonany częściowo", kind: "warn" };
  return { label: "Otwarty", kind: "open" };
};

interface Props {
  box: Box;
}

/**
 * Skrzynka dekretów — port „Przekazane do mnie" / zadań z dekretów z Magistratu.
 * „Do wykonania" = dekrety wydane do mnie (także kopie „do wiadomości" i „za
 * potwierdzeniem"); „Wydane" = dekrety, które ja wydałem, z tym, u kogo jest
 * przesyłka teraz. Kliknięcie wiersza otwiera przesyłkę w Przychodzących.
 */
const DecreesBox = ({ box }: Props) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const { data, isLoading } = decreeService.useGetBox(box, includeClosed);
  const items = useMemo<IDecreeBoxItem[]>(
    () => data?.data?.Items ?? [],
    [data]
  );

  const columns: GridColDef<IDecreeBoxItem>[] = useMemo(() => {
    const cols: GridColDef<IDecreeBoxItem>[] = [
      {
        field: "TypeLabel",
        headerName: "Rodzaj",
        width: 210,
        valueGetter: (_v, row) => row.TypeLabel ?? "",
      },
      {
        field: "ParcelTitle",
        headerName: "Przesyłka",
        flex: 1,
        minWidth: 240,
        valueGetter: (_v, row) =>
          row.ParcelTitle || row.Title || "(bez tytułu)",
      },
      {
        field: "ParcelNumber",
        headerName: "Nr",
        width: 120,
        valueGetter: (_v, row) => row.ParcelNumber ?? "",
      },
      {
        field: "ParcelSender",
        headerName: "Nadawca",
        width: 200,
        valueGetter: (_v, row) => row.ParcelSender ?? "",
      },
    ];

    if (box === "received") {
      cols.push({
        field: "CreatedByLabel",
        headerName: "Od kogo",
        width: 180,
        valueGetter: (_v, row) => row.CreatedByLabel ?? "",
      });
    } else {
      cols.push(
        {
          field: "ReceiverLabel",
          headerName: "Do kogo",
          width: 180,
          valueGetter: (_v, row) => row.ReceiverLabel ?? "",
        },
        {
          field: "ParcelOwnerLabel",
          headerName: "Teraz u",
          width: 180,
          valueGetter: (_v, row) => row.ParcelOwnerLabel ?? "",
        }
      );
    }

    cols.push(
      {
        field: "Deadline",
        headerName: "Termin",
        width: 120,
        valueGetter: (_v, row) => fmtDate(row.Deadline),
      },
      {
        field: "CreationTimestamp",
        headerName: "Wydano",
        width: 120,
        valueGetter: (_v, row) => fmtDate(row.CreationTimestamp),
      },
      {
        field: "Status",
        headerName: "Status",
        width: 180,
        sortable: false,
        renderCell: (params) => {
          const s = statusOf(params.row);
          return (
            <span className={`${styles.status} ${styles[s.kind]}`}>
              {s.label}
            </span>
          );
        },
      },
      {
        field: "Text",
        headerName: "Treść / powód",
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) =>
          params.row.Type === EDecreeType.Return ? (
            <span className={styles.reason}>{params.row.Text ?? ""}</span>
          ) : (
            params.row.Text ?? ""
          ),
      }
    );
    return cols;
  }, [box]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withId = items.map((i) => ({ ...i, id: i.Id }));
    if (!q) return withId;
    return withId.filter((r) =>
      [
        r.TypeLabel,
        r.ParcelTitle,
        r.Title,
        r.ParcelNumber,
        r.ParcelSender,
        r.CreatedByLabel,
        r.ReceiverLabel,
        r.Text,
      ].some((f) => (f ?? "").toLowerCase().includes(q))
    );
  }, [items, search]);

  const openParcel = (params: GridRowParams<IDecreeBoxItem>) => {
    navigate(
      `/packages/${INCOMING_FILTER_ID}/incoming/${ItemKind.Parcel}/${params.row.ObjectId}`
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>
          {box === "received" ? "Dekrety do wykonania" : "Wydane dekrety"}
        </span>
        <div className={styles.tools}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeClosed}
                onChange={(e) => setIncludeClosed(e.target.checked)}
              />
            }
            label="Pokaż zakończone"
          />
          <TextField
            size="small"
            placeholder="Szukaj na liście"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchBox}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>

      <div className={styles.grid}>
        <DataGridPremium
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
          }}
          localeText={plGridLocales}
          columnHeaderHeight={36}
          columns={columns}
          rows={rows}
          loading={isLoading}
          hideFooter
          disableAggregation
          disableColumnFilter
          disableRowSelectionOnClick
          onRowClick={openParcel}
        />
      </div>
    </div>
  );
};

export default DecreesBox;
